import Vue from 'vue';
import {
  isValidArray,
  isValidObject,
  stringToNumber,
  SuperDate,
} from '@razlet/utils';
import { PRICING_UID_REGEX } from '~/api/constants/pricing';
import { getRandomId } from '~/utils';
import {
  calculateDimensionVolume,
  createVolumeMask,
  DIMENSION_VOLUME_PRECISION,
  resolveVolumePrecision,
  roundVolume,
} from '~/utils/volume';

const isCabinet = process.env.PLATFORM === 'cabinet';

function countStartDate (_date) {
  const DAYS_BEFORE_DATE = 2;

  const date = new SuperDate(_date?.toServer(true));
  const _today = new Date();
  _today.setHours(0, 0, 0, 0);
  const today = new SuperDate(_today);

  const diffTime = Math.round(date.client - today.client);
  if (diffTime < 0) return today;

  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays >= DAYS_BEFORE_DATE) {
    date.client.setDate((date.client.getDate() - DAYS_BEFORE_DATE));
  }
  if (diffDays !== 0 && diffDays < DAYS_BEFORE_DATE) {
    date.client.setDate((date.client.getDate() - diffDays));
  }
  return date;
}

function countEndDate (_date, offset = 6) {
  const date = new SuperDate(_date?.toServer(true));
  date.client.setDate((date.client.getDate() + offset));
  return date;
}

function countRange (startDate, end) {
  const dates = [];
  const start = new SuperDate(startDate.toServer(true));
  start.client.setHours(0, 0, 0, 0);
  while (start.client <= end.client) {
    dates.push(new SuperDate(new Date(start.client)));
    start.client.setDate(start.client.getDate() + 1);
  }

  return dates;
}

export class Dimension {
  pieces = '';
  weight = '';
  width = '';
  height = '';
  length = '';
  uid = '';
  constructor (data) {
    this.uid = getRandomId();
    if (data) {
      this.pieces = data.pieces;
      this.weight = data.weight;
      this.width = data.width;
      this.height = data.height;
      this.length = data.length;
    }
  }

  get volume () {
    return roundVolume(calculateDimensionVolume(this), DIMENSION_VOLUME_PRECISION);
  }

  get volumeMask () {
    return createVolumeMask(DIMENSION_VOLUME_PRECISION);
  }

  isEmptyDimension () {
    return !this.pieces?.toString().trim() &&
           !this.weight?.toString().trim() &&
           !this.width?.toString().trim() &&
           !this.height?.toString().trim() &&
           !this.length?.toString().trim();
  }

  build () {
    const {
      weight,
      width,
      height,
      length,
      pieces,
    } = this;

    return {
      weight: stringToNumber(weight),
      width: stringToNumber(width),
      height: stringToNumber(height),
      length: stringToNumber(length),
      pieces: stringToNumber(pieces),
    };
  }
}

export default class BookingRequest {
  company = null;
  departure = null;
  arrival = null;

  todayDate = new SuperDate(new Date());
  dateBegin = new SuperDate(new Date());
  dateFinish = null;
  date = null;

  weight = '';
  volume = '';
  pieces = '';
  shcList = null;
  hsList = [];
  commodity = '';

  contractId = null;
  postNumber = '';
  correspondenceNumber = '';
  mAwb = null;
  prefix = null;
  fullMawb = null;
  fullContract = null;

  dimensions = [];
  defaultDimension = this.createDimension();

  raids = [];
  initialRaids = [];

  initialRoute = {
    departure: null,
    arrival: null,
  };

  isPost = false;

  isCorrespondence = false;

  isBlankUsed = false;

  validationType = 'pricing';

  canChangeDirection = true;
  canChangeCargoSizes = true;

  dateFilter = null;

  dateOptions = [];

  constructor (data, raids = '') {
    if (data) {
      if (isValidObject(data)) this.fill(data);
      else this.fillFromString(data, raids);
    }
    if (!isCabinet) this.refillFinish();
  }

  fill (data) {
    this.company = data.company || null;
    if (data.expeditor && !isCabinet) this.contractId = data.expeditor.id;

    this.departure = data.departure;
    this.arrival = data.arrival;

    if (data.bookings && data.bookings.length) {
      const firstDate = data.bookings[0].raid.departure.date.serverDate;
      if (isCabinet) this.date = new SuperDate(firstDate);
      else this.dateBegin = new SuperDate(firstDate);

      this.initialRaids = data.bookings
        .filter(booking => !booking.notActive)
        .map(booking => booking.raid.id);

      this.initialRoute = {
        departure: data.departure,
        arrival: data.arrival,
      };
    }

    this.pieces = data.cargo.pieces;
    this.weight = data.cargo.weight;
    this.volume = this.roundedVolumeValue(data.cargo.volume);

    this.shcList = data.cargo.shc;
    this.hsList = data.cargo.hs;
    this.commodity = data.cargo.commodity.name;

    this.dimensions = data.cargo.dimensions.map(dimension => this.createDimension(dimension));

    this.isPost = data.isPost;
    this.isCorrespondence = data.isCorrespondence;

    this.isBlankUsed = data.isBlankUsed;

    if (this.isPost) this.postNumber = data.postAwb;
    if (this.isCorrespondence) this.correspondenceNumber = data.correspondenceAwb;

    if (!Vue.prototype.$userData?.isAdmin && data.isAcceptedByFSUFOH) {
      this.canChangeDirection = false;
      this.canChangeCargoSizes = false;
    }
  }

  fillFromString (str, raids = '') {
    const match = str.match(PRICING_UID_REGEX);
    if (!match) return;
    const { departure, date, month, arrival, pieces, weight, volume, shc } = match.groups;

    this.departure = departure;
    this.arrival = arrival;

    const _date = stringToNumber(date);
    const _month = stringToNumber(month);
    const _year = _month < this.todayDate.month ? this.todayDate.year + 1 : this.todayDate.year;

    this.date = new SuperDate(new Date(_year, _month - 1, _date));

    this.pieces = stringToNumber(pieces) || '';
    this.weight = stringToNumber(weight) || '';
    this.volume = this.roundedVolumeValue(volume) || '';

    this.shcList = shc ? shc.match(/.{3}/g) : null;

    this.raids = raids.split('-');
  }

  refillFinish () {
    const finish = new Date(this.dateBegin.client);
    finish.setDate(finish.getDate() + 3);
    this.dateFinish = new SuperDate(finish);
  }

  fillDateOptions (result) {
    const { date, dateBegin, dateFinish } = this;
    const _dateBegin = isCabinet ? countStartDate(date) : dateBegin;
    const _dateFinish = isCabinet ? countEndDate(_dateBegin) : dateFinish;

    const dates = countRange(_dateBegin, _dateFinish);

    this.dateOptions = dates.map(date => {
      return {
        date,
        count: result.quantityByDate(date.dateUid),
      };
    });
    this.dateFilter = isCabinet ? date.dateUid : dateBegin.dateUid;
  }

  selectDate (value) {
    this.dateFilter = value;
  }

  setFullMawb (item) {
    this.fullMawb = item;
  }

  setFullContract (item) {
    this.fullContract = item;
  }

  swapAirports () {
    [this.departure, this.arrival] = [this.arrival, this.departure];
  }

  get volumeRoundingCompany () {
    return this.fullContract?.airlineCode ||
      this.company ||
      null;
  }

  get volumePrecision () {
    return resolveVolumePrecision({ company: this.volumeRoundingCompany });
  }

  get volumeMask () {
    return createVolumeMask(this.volumePrecision);
  }

  roundedVolumeValue (value) {
    return roundVolume(value, this.volumePrecision);
  }

  createDimension (data) {
    return new Dimension(data);
  }

  filterEmptyDimensions () {
    this.dimensions = this.dimensions.filter(dim => !dim.isEmptyDimension());
  }

  sumDimensions () {
    return {
      pieces: String(this.dimensions.reduce((sum, item) => {
        return sum + stringToNumber(item.pieces);
      }, 0)),
      weight: String(this.dimensions.reduce((sum, item) => {
        return sum + (stringToNumber(item.pieces) * stringToNumber(item.weight));
      }, 0)),
      // Round the total once; individual displayed dimensions are already rounded.
      volume: String(this.roundedVolumeValue(this.dimensions.reduce((sum, item) => {
        return sum + calculateDimensionVolume(item);
      }, 0))),
    };
  }

  fillDimensionsSum () {
    const sum = this.sumDimensions();

    this.pieces = sum.pieces;
    this.weight = sum.weight;
    this.volume = this.roundedVolumeValue(sum.volume);
  }

  get isRaids () {
    return isValidArray(this.raids) || isValidArray(this.initialRaids);
  }

  get isBooking () {
    return this.validationType === 'booking';
  }

  get isPricing () {
    return this.validationType === 'pricing';
  }

  get isRequirements () {
    return this.validationType === 'requirements';
  }

  get direction () {
    return `${this.departure} — ${this.arrival}`;
  }

  get prettyDate () {
    return this.date?.prettyDateWithoutCurrentYear;
  }

  get pricingQuery () {
    const {
      shcList,
      weight,
      volume,
      pieces,
      departure,
      arrival,
      date,
    } = this;

    const shr = shcList || [];

    const dateInfo = [
      date?.shortDateNoSeparators ?? this.dateBegin.shortDateNoSeparators,
    ];

    return [
      departure,
      ...dateInfo,
      arrival,
      'T',
      pieces || 0,
      'K',
      weight || 0,
      'MC',
      this.roundedVolumeValue(volume),
      ...shr,
    ].join('');
  }

  buildPricingCargo () {
    const {
      shcList,
      weight,
      volume,
      pieces,
    } = this;

    const request = {};
    if (shcList && shcList.length) request.shr = shcList.join(',');
    if (weight) request.weight = stringToNumber(weight);
    if (volume) request.volume = this.roundedVolumeValue(volume);
    if (pieces) request.pieces = stringToNumber(pieces);

    return request;
  }

  buildPricing () {
    const {
      departure,
      arrival,
      date,
      dateBegin,
      dateFinish,
      contractId,
    } = this;

    const _dateBegin = isCabinet && date ? countStartDate(date) : dateBegin;
    const _dateFinish = isCabinet && date ? countEndDate(_dateBegin) : dateFinish;

    const request = {
      contractId,
      departure,
      arrival,
      dateBegin: _dateBegin.toServer(false),
      dateFinish: _dateFinish.toServer(false),
      ...this.buildPricingCargo(),
    };

    return request;
  }

  buildPricingByRaids () {
    const {
      contractId,
      raids,
    } = this;

    const request = {
      contractId,
      raids,
      ...this.buildPricingCargo(),
    };

    return request;
  }

  buildRaids (withBooking) {
    return withBooking
      ? isValidArray(this.raids)
        ? this.raids
        : this.initialRaids
      : [];
  }

  buildRoute () {
    const { departure, arrival } = this;
    return isValidArray(this.raids)
      ? {
        departure,
        arrival,
      }
      : this.initialRoute.departure && this.initialRoute.arrival
        ? this.initialRoute
        : {
          departure,
          arrival,
        };
  }

  buildBaseBooking () {
    const {
      shcList,
      hsList,
      commodity,
      weight,
      volume,
      pieces,
      dimensions,
    } = this;

    const req = {
      shcList: shcList || [],
      hsList: hsList.length ? hsList.map(stringToNumber) : [],
      commodity: commodity || '',
      cargo: {
        weight: stringToNumber(weight),
        volume: this.roundedVolumeValue(volume),
        pieces: stringToNumber(pieces),
      },
      dimensions: dimensions
        .filter(dim => !dim.isEmptyDimension())
        .map(dim => dim.build()),
      route: this.buildRoute(),
    };

    return req;
  }

  buildEdit () {
    return {
      ...this.buildBaseBooking(),
      raids: this.buildRaids(true),
    };
  }

  buildBooking ({ withBooking, mAwb }) {
    const {
      fullMawb,
      contractId,
    } = this;

    return {
      ...this.buildBaseBooking(),
      mAwb: fullMawb?.mawb || mAwb,
      contractId,
      booking: withBooking,
      raids: this.buildRaids(withBooking),
    };
  }

  buildPostBooking ({ withBooking }) {
    const {
      postNumber,
      contractId,
    } = this;

    const req = {
      ...this.buildBaseBooking(),
      postNumber,
      contractId,
      booking: withBooking,
      raids: this.buildRaids(withBooking),
    };

    delete req.shcList;
    delete req.dimensions;
    delete req.commodity;

    return req;
  }

  buildCorrespondenceBooking ({ withBooking }) {
    const {
      contractId,
      correspondenceNumber,
    } = this;

    const req = {
      ...this.buildBaseBooking(),
      contractId,
      number: correspondenceNumber,
      booking: withBooking,
      raids: this.buildRaids(withBooking),
    };

    delete req.hsList;

    return req;
  }

  buildSplit (raidId) {
    const {
      pieces,
      volume,
      weight,
      raids,
    } = this;

    const req = {
      cargo: {
        pieces: stringToNumber(pieces),
        volume: this.roundedVolumeValue(volume),
        weight: stringToNumber(weight),
      },
      raidId: raids[0],
    };

    return req;
  }

  buildRequirements () {
    const {
      contractId,
      shcList,
      weight,
      volume,
      pieces,
      dimensions,
      raids,
    } = this;

    const req = {
      contractId: contractId,
      cargo: {
        weight: stringToNumber(weight),
        volume: this.roundedVolumeValue(volume),
        pieces: stringToNumber(pieces),
      },
      dimensions: dimensions
        .filter(dim => !dim.isEmptyDimension())
        .map(dim => dim.build()),
      shcList: shcList || [],
      raids: raids,
    };

    return req;
  }
}
